import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Search, BookOpen, Eye, EyeOff, Pencil, Trash2, Tag, Clock
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const KB_CATEGORIES = [
  { value: 'getting_started', label: 'Getting Started' },
  { value: 'features', label: 'Features' },
  { value: 'billing', label: 'Billing' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'api', label: 'API & Integrations' },
  { value: 'general', label: 'General' },
];

function timeAgo(d) { if (!d) return ''; const s = Math.floor((new Date() - new Date(d)) / 1000); if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; }

function ArticleModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ title: '', body: '', category: 'general', tags: '', published: true });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setForm({ title: editing.title || '', body: editing.body || '', category: editing.category || 'general', tags: (editing.tags || []).join(', '), published: editing.published !== false });
    else setForm({ title: '', body: '', category: 'general', tags: '', published: true });
  }, [editing, open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
      const { data } = editing
        ? await axios.put(`${API}/api/service/kb/articles/${editing.id}`, payload)
        : await axios.post(`${API}/api/service/kb/articles`, payload);
      toast.success(editing ? 'Article updated' : 'Article created');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-emerald-500" />{editing ? 'Edit' : 'New'} Article</DialogTitle><DialogDescription>Create knowledge base content</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="How to..." data-testid="article-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label><Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}><SelectTrigger data-testid="article-category"><SelectValue /></SelectTrigger><SelectContent>{KB_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={e => setForm(p => ({...p, tags: e.target.value}))} placeholder="crm, leads" /></div>
          </div>
          <div><Label>Content (Markdown supported)</Label><Textarea value={form.body} onChange={e => setForm(p => ({...p, body: e.target.value}))} rows={12} className="font-mono text-sm" placeholder="Write your article content here..." data-testid="article-body" /></div>
          <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={v => setForm(p => ({...p, published: v}))} /><Label>Published</Label></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-article-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Publish Article'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

function ArticleView({ article, onClose }) {
  if (!article) return null;
  // Basic markdown rendering
  const renderBody = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-gray-800 mt-4 mb-2">{line.slice(3)}</h2>;
      if (line.startsWith('# ')) return <h1 key={i} className="text-xl font-bold text-gray-900 mt-4 mb-2">{line.slice(2)}</h1>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-sm text-gray-600 leading-relaxed">{line}</p>;
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="article-view-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-gray-900 line-clamp-1">{article.title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8" data-testid="close-article-view"><BookOpen className="h-4 w-4" /></Button>
        </div>
        <div className="px-6 py-2 border-b flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs">{KB_CATEGORIES.find(c => c.value === article.category)?.label || article.category}</Badge>
          {(article.tags || []).map(t => <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>)}
          <span className="text-[10px] text-gray-400 ml-auto">By {article.created_by_name} · {timeAgo(article.created_at)}</span>
        </div>
        <div className="px-6 py-5">{renderBody(article.body || '')}</div>
      </div>
    </div>
  );
}

export default function KnowledgeBasePage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchArticles = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCat && filterCat !== 'all') params.category = filterCat;
      const { data } = await axios.get(`${API}/api/service/kb/articles`, { params,  });
      setArticles(data);
    } catch { toast.error('Failed to load articles'); }
    finally { setLoading(false); }
  }, [search, filterCat]);
  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const handleSaved = (a, isUpdate) => { if (isUpdate) setArticles(p => p.map(x => x.id === a.id ? a : x)); else setArticles(p => [a, ...p]); };
  const handleDelete = async (id) => { if (!window.confirm('Delete article?')) return; try { await axios.delete(`${API}/api/service/kb/articles/${id}`); setArticles(p => p.filter(a => a.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); } };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="knowledge-base-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><BookOpen className="h-5 w-5 text-emerald-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Knowledge Base<PageInfoTooltip pageKey="knowledge-base" /></h1><p className="text-xs text-gray-400">{articles.length} articles · {articles.filter(a => a.published).length} published</p></div></div>
        <div className="flex items-center gap-2">
          <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-40 h-9 text-sm" data-testid="kb-search" /></div>
          <Select value={filterCat} onValueChange={setFilterCat}><SelectTrigger className="w-36 h-9 text-sm"><SelectValue placeholder="Category" /></SelectTrigger><SelectContent><SelectItem value="all">All Categories</SelectItem>{KB_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 h-9 gap-1.5" data-testid="new-article-btn"><Plus className="h-4 w-4" /> New Article</Button>
        </div>
      </div>

      {loading ? <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div> : articles.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300"><CardContent className="p-10 text-center"><BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-700 mb-1">No articles yet</h3><Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 mt-3"><Plus className="h-4 w-4" /> Write Article</Button></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map(a => (
            <Card key={a.id} className="border-gray-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setViewing(a)} data-testid={`article-card-${a.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-gray-900 line-clamp-2">{a.title}</h3>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-[9px]">{KB_CATEGORIES.find(c => c.value === a.category)?.label || a.category}</Badge>
                      {!a.published && <Badge className="bg-yellow-100 text-yellow-700 text-[9px]">Draft</Badge>}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 line-clamp-3 mb-2">{(a.body || '').replace(/[#*]/g, '').slice(0, 150)}...</p>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {(a.tags || []).slice(0, 3).map(t => <Badge key={t} variant="outline" className="text-[9px] px-1.5">{t}</Badge>)}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{timeAgo(a.created_at)}</span>
                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditing(a); setModalOpen(true); }}><Pencil className="h-3 w-3 text-gray-400" /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDelete(a.id)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ArticleModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
      {viewing && <ArticleView article={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
